// src/utils/templateAdapter.js
// Transform assessment templates to legacy config format
// Ensures ZERO changes to assessment components

/**
 * Transform template structure to legacy config format
 * This allows all existing components to work without modification
 *
 * @param {Object} template - Template from Supabase
 * @returns {Object} Config object matching legacy format
 */
export const transformTemplateToLegacyConfig = (template) => {
  if (!template || !template.taxonomy) {
    console.warn('[templateAdapter] Invalid template structure:', template);
    return {};
  }

  return {
    // Phase 0: Context Discovery
    contextForm: template.taxonomy.contextFields || {},

    // Phase 1-2: Risk Discovery & Cause Analysis
    riskAssessmentConfig: {
      riskAreas: template.taxonomy.riskAreas || {},
      rootCauses: template.taxonomy.rootCauses || {},
      causeEvaluationCriteria: template.taxonomy.causeEvaluationCriteria || {}
    },

    // Phase 3: Risk Evaluation
    riskEvaluationForm: template.evaluationCriteriaRefs?.riskEvaluation || [],

    // Phase 4: Action Planning / Control Evaluation
    controlEvaluation: template.evaluationCriteriaRefs?.controlEvaluation || [],

    // Phase 5: Security Assessment
    securityConfig: {
      taxonomy: template.taxonomy.securityTaxonomy || {},
      impactCategories: template.taxonomy.securityImpactCategories || {},
      vulnerabilityAssessment: template.taxonomy.vulnerabilityAssessment || {}
    },

    // Evaluation criteria references (will be fetched separately by ID)
    evaluationCriteriaRefs: template.evaluationCriteriaRefs || {}
  };
};

/**
 * Extract specific config section from template
 *
 * @param {Object} template - Template from Supabase
 * @param {string} configName - Config section name (e.g., 'contextForm', 'riskAssessmentConfig')
 * @returns {Object} Specific config section
 */
export const getConfigFromTemplate = (template, configName) => {
  const transformed = transformTemplateToLegacyConfig(template);
  return transformed[configName] || null;
};

/**
 * Get risk areas from template (convenience method)
 *
 * @param {Object} template - Template from Supabase
 * @returns {Object} Risk areas object
 */
export const getRiskAreasFromTemplate = (template) => {
  return template?.taxonomy?.riskAreas || {};
};

/**
 * Get context fields from template (convenience method)
 *
 * @param {Object} template - Template from Supabase
 * @returns {Object} Context fields object
 */
export const getContextFieldsFromTemplate = (template) => {
  return template?.taxonomy?.contextFields || {};
};

/**
 * Get root causes from template (convenience method)
 *
 * @param {Object} template - Template from Supabase
 * @returns {Object} Root causes object
 */
export const getRootCausesFromTemplate = (template) => {
  return template?.taxonomy?.rootCauses || {};
};

/**
 * Get security taxonomy from template (convenience method)
 *
 * @param {Object} template - Template from Supabase
 * @returns {Object} Security taxonomy object
 */
export const getSecurityTaxonomyFromTemplate = (template) => {
  return template?.taxonomy?.securityTaxonomy || {};
};

/**
 * Get security config in merged format for useMergedSecurityForm
 * Combines securityTaxonomy, vulnerabilityAssessment, and impactCategories
 *
 * @param {Object} template - Template from Supabase
 * @returns {Object} Security config in format expected by useMergedSecurityForm
 */
export const getSecurityConfigFromTemplate = (template) => {
  const securityConfig = getConfigFromTemplate(template, 'securityConfig');

  return {
    security: securityConfig?.taxonomy || [],
    vulnerabilityAssessmentForm: securityConfig?.vulnerabilityAssessment || null,
    securityImpactAssessment: securityConfig?.impactCategories || null
  };
};

/**
 * Extract impact categories from template taxonomy
 * Falls back to global legacy themes if template doesn't have impact categories
 *
 * Impact categories are used during risk evaluation to assess the potential
 * consequences of risks across different dimensions (e.g., Financial, Reputational, etc.)
 *
 * This replaces the legacy "risk_themes" system with template-based categories.
 *
 * @param {Object} template - Full template object from Supabase
 * @param {Array} legacyThemes - Global fallback themes from global_configs
 * @returns {Array} Impact categories in themes format for backward compatibility
 *
 * @example
 * // Template with impact categories
 * const categories = getImpactCategoriesFromTemplate(template, legacyThemes);
 * // Returns: [
 * //   { theme_id: 15, name: "Public Services Access", description: "..." },
 * //   { theme_id: 16, name: "Health & Wellbeing", description: "..." }
 * // ]
 */
export const getImpactCategoriesFromTemplate = (template, legacyThemes = []) => {
  if (!template) {
    console.log('[templateAdapter] No template provided, using legacy themes');
    return legacyThemes;
  }

  const impactCategories = template.taxonomy?.impactCategories;

  if (!impactCategories || impactCategories.length === 0) {
    console.log('[templateAdapter] No impact categories in template, using legacy themes');
    return legacyThemes;
  }

  console.log('[templateAdapter] Using template impact categories:', impactCategories.length);

  // Transform template format to themes format for backward compatibility
  // theme_id is used throughout the codebase for evaluationData structure
  return impactCategories
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(category => ({
      theme_id: category.id,
      name: category.name,
      description: category.description
    }));
};
